import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  alpha,
  Chip
} from "@mui/material";
import { NicheJobData } from "../niche-pig.slice";
import ResultsMoreButton from "./results-more-button";

const NichePigResultsTable = (props: { hideButtons?: boolean; toolData: NicheJobData | null }) => {
  const theme = useTheme();
  const results = props.toolData?.data.niches;

  if (!results) {
    return <></>;
  }

  const getScoreChip = (num: number) => {
    const getColor = (): { bg: string; text: string } => {
      if (num >= 8) {
        return { 
          bg: alpha(theme.palette.success.main, 0.1), 
          text: theme.palette.success.dark 
        };
      }
      if (num >= 6) {
        return { 
          bg: alpha(theme.palette.warning.main, 0.1), 
          text: theme.palette.warning.dark 
        };
      }
      if (num >= 4) {
        return { 
          bg: alpha(theme.palette.info.main, 0.1), 
          text: theme.palette.info.dark 
        };
      }
      return { 
        bg: alpha(theme.palette.error.main, 0.1), 
        text: theme.palette.error.dark 
      };
    };

    const colors = getColor();
    
    return (
      <Chip
        label={num.toFixed(1)}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          fontWeight: 600,
          minWidth: 50
        }}
      />
    );
  };

  const getCircularProgressBar = (num: number) => {
    const getColor = (num: number): string => {
      if (num >= 8) return theme.palette.success.main;
      if (num >= 6) return theme.palette.warning.main;
      if (num >= 4) return theme.palette.info.main;
      return theme.palette.error.main;
    };
    
    return (
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress 
          variant="determinate" 
          value={Math.ceil((num / 10) * 100)} 
          size={44}
          thickness={4}
          sx={{ 
            color: getColor(num),
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round'
            }
          }} 
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography 
            variant="caption" 
            component="div" 
            sx={{ 
              fontWeight: 600,
              color: theme.palette.text.primary 
            }}
          >
            {num.toFixed(1)}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <TableContainer 
      component={Paper} 
      elevation={0}
      sx={{ 
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        overflow: 'hidden'
      }}
    >
      <Table aria-label="niche results table">
        <TableHead>
          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
            <TableCell sx={{ fontWeight: 600 }}>Niche</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Subniches</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Affiliate Products</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Services</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Search</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Monetization</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Passion</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Overall Score</TableCell>
            {!props.hideButtons && <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>}
          </TableRow>
        </TableHead>

        <TableBody>
          {[...results]
            .sort((a, b) => {
              return (
                (b.audiencePassion + b.monetizationPotential + b.searchPotential) / 3 -
                (a.audiencePassion + a.monetizationPotential + a.searchPotential) / 3
              );
            })
            .map((result, index) => {
              const overallScore = (
                Number(result.searchPotential) +
                Number(result.monetizationPotential) +
                Number(result.audiencePassion)
              ) / 3;
              
              return (
                <TableRow 
                  key={index} 
                  sx={{ 
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.02)
                    },
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body2" fontWeight={600}>
                      {result.niche}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {result.subniches.join(", ")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {result.products.join(", ")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {result.services.join(", ")}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {getCircularProgressBar(Number(result.searchPotential))}
                  </TableCell>
                  <TableCell align="center">
                    {getCircularProgressBar(Number(result.monetizationPotential))}
                  </TableCell>
                  <TableCell align="center">
                    {getCircularProgressBar(Number(result.audiencePassion))}
                  </TableCell>
                  <TableCell align="center">
                    {getCircularProgressBar(overallScore)}
                  </TableCell>
                  {!props.hideButtons && (
                    <TableCell>
                      <ResultsMoreButton niche={result.niche} />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default NichePigResultsTable;
